import { BookingSessionStoreNS } from '../store/bookingSession';

import { ErrorMessage } from './amadeusErrors';

export namespace RepricePnrNS {
  export namespace RequestNS {
    export interface TicketRepriceWithoutPnrRequest {
      passengerGroups: PassengerGroup[];
      segmentGroups: SegmentGroup[];
      documentDetails: DocumentDetail[];
      pricingOptions: PricingOption[];
    }

    export interface TicketRepriceWithPnrRequest {
      documentDetails: DocumentDetail[];
      pricingOptions: PricingOption[];
    }

    export interface DocumentDetail {
      transactionIdentifierNumber: number;
      number: string;
      type: string;
    }

    export interface PricingOption {
      optionKey: string;
      paxSegTstReferences?: PaxSegTstReference[];
      optionCriteriaDetails?: OptionCriteriaDetail[];
    }

    export interface OptionCriteriaDetail {
      attributeType: string;
    }

    export interface PaxSegTstReference {
      type: string;
      value: string;
    }

    export interface PassengerGroup {
      numberOfUnits: number;
      travellerDetails: TravellerDetail[];
      discountPtcValueQualifier: string;
    }

    export interface TravellerDetail {
      measurementValue: number;
      surname: string;
      firstName: string;
    }

    export interface SegmentGroup {
      boardOff: BoardOff;
      segmentInformation: SegmentInformation[];
    }

    export interface BoardOff {
      origin: string;
      destination: string;
    }

    export interface SegmentInformation {
      departureDate: string;
      arrivalDate: string;
      arrivalTime?: string;
      departureTime?: string;
      boardPoint: string;
      offPoint: string;
      marketingCompany: string;
      flightNumber: number;
      bookingClass: string;
      flightIndicator: string;
      itemNumber: number;
    }
  }

  export namespace ResponseNS {
    export interface Response {
      ticketGroup: TicketGroup;
      fareList: FareList[];
      errorMessage: ErrorMessage;
      errorInformation: ErrorInformation[] | null;
      pnrHeaders: PnrHeader[];
      tstInformation: TstInformation[];
    }

    export interface TstInformation {
      tstReference: TstReference;
      paxReferences: PaxReference[];
      segReferences: SegReference[];
    }
    export interface TstReference {
      referenceType: string;
      uniqueReference: number;
    }

    export interface PaxReference {
      refQualifier: string;
      refNumber: number;
    }

    export interface SegReference {
      refQualifier: string;
      refNumber: number;
    }

    export interface AmadeusHeaders {
      sessionId: string;
      securityToken: string;
      sequenceNumber: number;
    }

    export interface FareList {
      pricingInformation: PricingInformation;
      paxSegReferences: Reference[];
      fareDataInformation: FareDataInformation;
      taxInformation: TaxInformation[];
      originDestinationCityCodes: string[];
      segmentInformation: SegmentInformation[];
      automaticReissueInfo: AutomaticReissueInfo;
      fareComponentDetailsGroups: FareComponentDetailsGroup[];
    }

    export interface AutomaticReissueInfo {
      ticketInfoDocumentDetails: TicketInfoDocumentDetails;
      couponNumbers: string[];
      baseFareInfoMonetaryDetails: MonetaryDetails;
      firstDpiGroup: FirstDPIGroup;
      secondDpiGroup: SecondDPIGroup;
    }

    export interface MonetaryDetails {
      typeQualifier: string;
      amount: string;
      currency: string;
    }

    export interface FirstDPIGroup {
      reIssuePenaltyPenDisData: PenaltyPenDisData;
      reIssueInfo: Info;
      oldTaxInfoMonetaryDetails: OldTaxInfoMonetaryDetails;
      reissueBalanceInfo: Info;
    }

    export interface OldTaxInfoMonetaryDetails {
      typeQualifier: string;
      amount: number;
      currency: string;
    }

    export interface Info {
      monetaryDetails: OldTaxInfoMonetaryDetails;
      otherMonetaryDetails: OldTaxInfoMonetaryDetails[];
    }

    export interface PenaltyPenDisData {
      penaltyAmount: number;
      penaltyCurrency: string;
    }

    export interface SecondDPIGroup {
      penaltyPenDisData: PenaltyPenDisData;
      residualValueInfo: Info;
      oldTaxInfo: Info;
      issueBalanceInfo: Info;
    }

    export interface TicketInfoDocumentDetails {
      number: string;
      type: string;
    }

    export interface FareComponentDetailsGroup {
      fareComponentIDNumber: string[];
      marketFareComponent: MarketFareComponent;
      monetaryDetails: MonetaryDetails;
      fareBasisRateTariffClass: string;
      discountDetailFareQualifiers: string[];
      fareFamilyName: string;
      fareFamilyOwnerOtherCompany: string;
      couponDetailsGroupProductIdReferenceDetails: CouponDetailsGroupProductIDReferenceDetail[];
    }

    export interface CouponDetailsGroupProductIDReferenceDetail {
      type: string;
      value: string;
    }

    export interface MarketFareComponent {
      boardPointDetails: string;
      offPointDetails: string;
    }

    export interface FareDataInformation {
      fareDataMainInformationQualifier: string;
      fareDataSupInformation: FareDataSupInformation[];
    }

    export interface FareDataSupInformation {
      fareDataQualifier: string;
      fareAmount: number;
      fareCurrency: string;
    }

    export interface Reference {
      refQualifier: string;
      refNumber: number;
    }

    export interface PricingInformation {
      tstIndicator: string;
      fcmi: string;
    }

    export interface SegmentInformation {
      connexType: string;
      fareBasisDetails: FareBasisDetails;
      validityInformation: ValidityInformation[];
      bagAllowanceDetails: BagAllowanceDetails;
      segmentReferences: Reference[];
      sequenceNumber: number;
    }

    export interface BagAllowanceDetails {
      baggageQuantity: number;
      baggageType: string;
    }

    export interface FareBasisDetails {
      primaryCode: string;
      fareBasisCode: string;
      discTktDesignator: string;
    }

    export interface ValidityInformation {
      businessSemantic: BusinessSemantic;
      dateTime: Date;
    }

    export enum BusinessSemantic {
      A = 'A',
      B = 'B',
    }

    export interface TaxInformation {
      taxDetails: TaxDetails;
      amountDetailsFareDataMainInformation: AmountDetailsFareDataMainInformation;
    }

    export interface AmountDetailsFareDataMainInformation {
      fareDataQualifier: FareDataQualifier;
      fareAmount: string;
      fareCurrency: string;
    }

    export enum FareDataQualifier {
      Tax = 'TAX',
    }

    export interface TaxDetails {
      taxIdentifier: TaxIdentifier;
      isoCountry: string;
      taxNature: string;
    }

    export enum TaxIdentifier {
      PD = 'PD',
      X = 'X',
    }

    export interface TicketGroup {
      controlNumber: string;
      errorMessage: ErrorMessage | null;
    }

    export interface PnrHeader {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      references: any[];
    }

    export interface ErrorInformation {
      errorOrWarningCodeDetails: ErrorOrWarningCodeDetails;
      errorWarningDescription: ErrorWarningDescription;
    }

    export interface ErrorOrWarningCodeDetails {
      errorCode: string;
      errorCategory: string;
      errorCodeOwner: string;
    }

    export interface ErrorWarningDescription {
      textSubjectQualifier: string;
      informationType: null;
      language: null;
      source: string;
      encoding: string;
      freeText: string[];
    }
  }

  export const enum RepriceType {
    'RepriceWithoutTST' = 'RepriceWithoutTST',
    'RepriceWithTST' = 'RepriceWithTST',
  }
}

export interface RepriceRequest {
  rebookingSession: BookingSessionStoreNS.BookingSession;
}
